import { cloneDeep, debounce, Logger, omit, pick } from "lib";
import { TEXTBOX_COLLECTION_PROPAGATION_ACTIONS } from "lib/constants";
import { flipSelectionOnAxis } from "views/components/Editor/utils";
import { onColorChange } from "views/components/Editor/editorOps/Editor.onColorChange";
import ActionBarOps from "views/components/Editor/actionbar/ActionBarOps";
import { removeItem } from "lib/array/array";
import { EditorTableOps } from "views/components/Editor/editorOps/index";
import {
  adjustKerningOverride,
  adjustKerningOverrideRange,
  compactKerningOverrides,
  mergeKerningOverride,
  plainTextFromHTML
} from "lib/kerning";
import { resolveKerningTarget } from "lib/kerning/kerningTargets";
import { isDielineGuideElement } from "lib/dielineGuides";

const KERNING_COMMIT_DELAY_MS = 500;
const KERNING_HUD_HIDE_DELAY_MS = 1000;

const getCaretAnchorPoint = () => {
  if (typeof window === "undefined" || !window.getSelection) return null;
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  const rect = range.getBoundingClientRect();
  if (
    rect.width === 0 &&
    rect.height === 0 &&
    rect.left === 0 &&
    rect.top === 0
  ) {
    // Collapsed caret in some browsers reports a zero rect; fall back to the
    // start container's parent rect, anchored at its horizontal center so the
    // HUD doesn't jump to the corner.
    const node =
      range.startContainer.nodeType === Node.ELEMENT_NODE
        ? range.startContainer
        : range.startContainer.parentNode;
    if (!node || !node.getBoundingClientRect) return null;
    const fallback = node.getBoundingClientRect();
    return { x: fallback.left + fallback.width / 2, y: fallback.top };
  }
  return { x: rect.left + rect.width / 2, y: rect.top };
};

const ensureKerningHudHide = ctx => {
  if (!ctx._kerningHudHide) {
    ctx._kerningHudHide = debounce(() => {
      ctx.props.updateContextState({ kerningHud: null });
    }, KERNING_HUD_HIDE_DELAY_MS);
  }
  return ctx._kerningHudHide;
};

const dispatchKerningHud = (ctx, value) => {
  const anchor = getCaretAnchorPoint();
  if (!anchor) return;
  ctx.props.updateContextState({
    kerningHud: { visible: true, value, x: anchor.x, y: anchor.y }
  });
  ensureKerningHudHide(ctx)();
};

// Snapshot/preview/commit map keys are scoped per target so a textbox burst
// and a cell burst (or two cells) never collide on the same debounce slot.
const targetKey = target =>
  target.kind === "table2Cell"
    ? `${target.itemId}:${target.cellId}`
    : target.itemId;

const targetSelectedItem = target => [
  { itemId: target.itemId, pageId: target.pageId }
];

// Locate the persisted text-bearing unit for the target — element for
// textboxes, cell for table2 cells. Returns null when the unit is missing.
const readPersistedUnit = (props, target) => {
  const element = props.designData && props.designData.elements[target.itemId];
  if (!element) return null;
  if (target.kind === "textbox") return element;
  const cell = element.cells && element.cells[target.cellId];
  return cell || null;
};

// Returns { has, overrides } for a preview entry's kerning slot. For
// textboxes the slot is `kerningOverrides`; for cells it lives at
// `cellKerningOverrides[cellId]` so a shallow spread of the entry onto
// elementData can't clobber the cells map.
const readPreviewKerningSlot = (idPreview, target) => {
  if (!idPreview) return { has: false };
  if (target.kind === "textbox") {
    return "kerningOverrides" in idPreview
      ? { has: true, overrides: idPreview.kerningOverrides }
      : { has: false };
  }
  const cellMap = idPreview.cellKerningOverrides;
  return cellMap && target.cellId in cellMap
    ? { has: true, overrides: cellMap[target.cellId] }
    : { has: false };
};

// Returns the next idPreview after writing `overrides` into the target's slot.
const writePreviewKerningSlot = (idPreview, target, overrides) => {
  if (target.kind === "textbox") {
    return { ...idPreview, kerningOverrides: overrides };
  }
  return {
    ...idPreview,
    cellKerningOverrides: {
      ...idPreview.cellKerningOverrides,
      [target.cellId]: overrides
    }
  };
};

// Returns the next idPreview with the target's slot removed, or null if the
// slot wasn't present (caller should no-op).
const clearPreviewKerningSlot = (idPreview, target) => {
  if (target.kind === "textbox") {
    return "kerningOverrides" in idPreview
      ? omit(idPreview, ["kerningOverrides"])
      : null;
  }
  const cellMap = idPreview.cellKerningOverrides;
  if (!cellMap || !(target.cellId in cellMap)) return null;
  const nextCellMap = { ...cellMap };
  delete nextCellMap[target.cellId];
  const next = { ...idPreview };
  if (Object.keys(nextCellMap).length === 0) {
    delete next.cellKerningOverrides;
  } else {
    next.cellKerningOverrides = nextCellMap;
  }
  return next;
};

// Replace or drop an item-level preview entry; if the next entry is empty,
// remove the key from allPreviews instead of leaving a hollow object behind.
const replaceOrDropPreviewEntry = (allPreviews, itemId, nextIdPreview) => {
  const next = { ...allPreviews };
  if (Object.keys(nextIdPreview).length === 0) {
    delete next[itemId];
  } else {
    next[itemId] = nextIdPreview;
  }
  return next;
};

// Build a fresh cells map by shallow-merging `partial` onto the target cell.
// Reads selectedItems[i].preview.cells per-cell on top of designData so any
// in-flight typing on a sibling or target cell survives the dispatch — without
// this overlay, the kerning commit's onSelectedElementsAttributesChange resets
// preview to {} and unflushed cell-value edits never reach designData.
const buildCellsWith = (ctx, target, partial) => {
  const element =
    ctx.props.designData && ctx.props.designData.elements[target.itemId];
  const persistedCells = (element && element.cells) || {};
  const liveItem =
    ctx.props.selectedItems &&
    ctx.props.selectedItems.find(item => item.itemId === target.itemId);
  const previewCells = liveItem && liveItem.preview && liveItem.preview.cells;
  const baseCells = { ...persistedCells };
  if (previewCells) {
    Object.keys(previewCells).forEach(cellId => {
      baseCells[cellId] = { ...baseCells[cellId], ...previewCells[cellId] };
    });
  }
  const baseCell = baseCells[target.cellId] || {};
  return {
    ...baseCells,
    [target.cellId]: { ...baseCell, ...partial }
  };
};

const buildCommitAttributes = (ctx, target, kerningOverrides) =>
  target.kind === "textbox"
    ? { kerningOverrides }
    : { cells: buildCellsWith(ctx, target, { kerningOverrides }) };

const buildLetterSpacingAttributes = (ctx, target, letterSpacing) =>
  target.kind === "textbox"
    ? { letterSpacing }
    : { cells: buildCellsWith(ctx, target, { letterSpacing }) };

const readActiveOverrides = (props, target) => {
  const previewEntry =
    props.elementPreviews && props.elementPreviews[target.itemId];
  const slot = readPreviewKerningSlot(previewEntry, target);
  if (slot.has) return slot.overrides;
  const unit = readPersistedUnit(props, target);
  return unit && unit.kerningOverrides;
};

// Resolve the plain-text content of the active target at the time of the
// call. Reads selectedItems[i].preview first (where Textbox.onChange and
// Table2Cell.onChange write during a typing burst); falls back to designData,
// which is only refreshed at edit-mode-end. Both snapshot-time and commit-time
// must use the same resolution so compactKerningOverrides remaps against
// matching before/after texts.
const resolveActiveText = (ctx, target) => {
  const liveItem =
    ctx.props.selectedItems &&
    ctx.props.selectedItems.find(item => item.itemId === target.itemId);
  const preview = liveItem && liveItem.preview;
  let previewValue;
  if (target.kind === "textbox") {
    previewValue = preview && preview.value;
  } else {
    const previewCell =
      preview && preview.cells && preview.cells[target.cellId];
    previewValue = previewCell && previewCell.value;
  }
  if (typeof previewValue === "string") return plainTextFromHTML(previewValue);
  const unit = readPersistedUnit(ctx.props, target);
  return plainTextFromHTML((unit && unit.value) || "");
};

// Callers must alignOverridesToLiveText before writing — re-snapshotting
// without realigning would re-interpret existing overrides under new coords.
const stampKerningSnapshot = (ctx, target) => {
  if (!ctx._kerningSnapshots) ctx._kerningSnapshots = {};
  ctx._kerningSnapshots[targetKey(target)] = resolveActiveText(ctx, target);
};

// Recompact overrides from the previous coordinate system (snapshot if one
// exists, else persisted designData) to the current live text.
const alignOverridesToLiveText = (ctx, target, overrides) => {
  if (!overrides) return overrides;
  const snapshot =
    ctx._kerningSnapshots && ctx._kerningSnapshots[targetKey(target)];
  let oldText;
  if (typeof snapshot === "string") {
    oldText = snapshot;
  } else {
    const unit = readPersistedUnit(ctx.props, target);
    oldText = plainTextFromHTML((unit && unit.value) || "");
  }
  const liveText = resolveActiveText(ctx, target);
  if (oldText === liveText) return overrides;
  return compactKerningOverrides(oldText, liveText, overrides) || {};
};

const writePreviewOverrides = (ctx, target, overrides) => {
  const allPreviews = ctx.props.elementPreviews || {};
  const idPreview = allPreviews[target.itemId] || {};
  stampKerningSnapshot(ctx, target);
  ctx.props.updateContextState({
    elementPreviews: {
      ...allPreviews,
      [target.itemId]: writePreviewKerningSlot(idPreview, target, overrides)
    }
  });
};

const clearPreviewOverrides = (ctx, target) => {
  if (ctx._kerningSnapshots) delete ctx._kerningSnapshots[targetKey(target)];
  const allPreviews = ctx.props.elementPreviews || {};
  const idPreview = allPreviews[target.itemId];
  if (!idPreview) return;
  const nextIdPreview = clearPreviewKerningSlot(idPreview, target);
  if (!nextIdPreview) return;
  ctx.props.updateContextState({
    elementPreviews: replaceOrDropPreviewEntry(
      allPreviews,
      target.itemId,
      nextIdPreview
    )
  });
};

// Trailing-edge commit. Stored on the Editor instance so each target keeps
// its own debouncer; a burst of keystrokes commits the *final* preview value
// rather than the snapshot at burst start. Recompacts against the current text
// so character edits during the debounce window can't persist stale indices.
const ensureKerningCommit = (ctx, target) => {
  if (!ctx._kerningCommits) ctx._kerningCommits = {};
  const key = targetKey(target);
  if (!ctx._kerningCommits[key]) {
    ctx._kerningCommits[key] = debounce(() => {
      // Bail out when the target has gone away (table deleted / design
      // closed within the debounce window). Without this, buildCellsWith
      // would assemble a single-cell `cells` map from an empty designData
      // and dispatching it as the full cells attribute would clobber every
      // sibling cell on whatever element happens to occupy itemId now.
      if (!readPersistedUnit(ctx.props, target)) return;
      const previewEntry =
        ctx.props.elementPreviews && ctx.props.elementPreviews[target.itemId];
      const slot = readPreviewKerningSlot(previewEntry, target);
      if (!slot.has) return;
      const snapshotText = ctx._kerningSnapshots && ctx._kerningSnapshots[key];
      const currentText = resolveActiveText(ctx, target);
      const final =
        slot.overrides && typeof snapshotText === "string"
          ? compactKerningOverrides(snapshotText, currentText, slot.overrides)
          : slot.overrides;
      clearPreviewOverrides(ctx, target);
      EditorElementsOps.onSelectedElementsAttributesChange.call(
        ctx,
        buildCommitAttributes(ctx, target, final),
        targetSelectedItem(target)
      );
    }, KERNING_COMMIT_DELAY_MS);
  }
  return ctx._kerningCommits[key];
};

/**
 * Enqueue "Update Collection" propagation for a batch of element previews
 * (typically from onColorChange). Each entry can carry its own attribute
 * values, and the replay handler applies one attribute payload uniformly to
 * all matched siblings, so we enqueue one action per element to keep
 * per-element values intact. Preview keys that aren't element ids
 * (e.g. pageId for backgroundColor) are skipped via the designData.elements
 * lookup.
 */
const enqueuePreviewPropagation = (
  updatedDesignData,
  elementPreviews,
  containerProps
) => {
  if (!containerProps || !containerProps.addActionToPropagationQueue) return;
  if (
    !containerProps.collectionDesigns ||
    containerProps.collectionDesigns.length <= 1
  ) {
    return;
  }

  Object.entries(elementPreviews).forEach(([elementId, attributes]) => {
    const element = updatedDesignData.elements[elementId];
    if (!element || !element.type) return;
    if (!attributes || !Object.keys(attributes).length) return;

    ActionBarOps.enqueueAttributePropagation({
      designData: updatedDesignData,
      selectedItems: [
        {
          itemId: elementId,
          pageId: updatedDesignData.getElementPageId(elementId)
        }
      ],
      attributes,
      collectionDesigns: containerProps.collectionDesigns,
      addActionToPropagationQueue: containerProps.addActionToPropagationQueue
    });
  });
};

const EditorElementsOps = {
  /**
   * Align the selected items according to the alignment parameter provided.
   * @param alignment - Alignment request for the selected elements.
   * @param {object} containerProps - Props of the calling container.
   */
  alignElements(alignment, containerProps) {
    Logger.info("EditorElementsOps.alignElements called");
    const { designData, selectedItems } = containerProps;

    const updatedDesignData = designData.alignElements(
      selectedItems,
      alignment
    );

    containerProps.onSave(updatedDesignData, {});
    containerProps.updateContextState({
      designData: updatedDesignData
    });
  },

  /**
   * Distribute the selected items along the axis provided.
   * @param axis - axis which the elements will be evenly distributed across.
   * @param {object} containerProps - Props of the calling container.
   */
  distributeElements(axis, containerProps) {
    Logger.info("EditorElementsOps.alignElements called");
    const { designData, selectedItems } = containerProps;

    const updatedDesignData = designData.distributeElements(
      selectedItems,
      axis
    );

    containerProps.onSave(updatedDesignData, {});
    containerProps.updateContextState({
      designData: updatedDesignData
    });
  },

  /**
   * Copies the selected items and returns and object with the newly updated design data and the
   * copied elements as the newly selectedItems.
   *
   * @param {Array} selectedItems - Current selected items.
   * @param {object} designData - Design data to operate on.
   * @return {object} containing the updated design data and the copied elements as the selectedItems
   */
  copyElements(selectedItems, designData) {
    // guard case for if copy is initiated with no selection
    if (!selectedItems.length) return;

    const { designUpdated, newIds } = designData.copyElements({
      elements: selectedItems
    });

    const newlySelectedItems = newIds.map(newElementId => ({
      itemId: newElementId,
      groupId: null,
      pageId: selectedItems[0].pageId,
      preview: {}
    }));

    return {
      designData: designUpdated,
      selectedItems: newlySelectedItems
    };
  },

  /**
   * Handle the color attribute change and save the design.
   *
   * @param {object} attributes - Color attributes to change.
   * @param {object} containerProps - Props of the calling container.
   */
  handleColorChange(attributes, containerProps) {
    const {
      context,
      currentPageId,
      designData,
      selectedItems,
      elementPreviews: propsElementPreviews,
      smartTextState
    } = containerProps;
    const elementPreviews = onColorChange({
      ...attributes,
      context,
      currentPageId,
      designData,
      selectedItems,
      stateElementPreviews: propsElementPreviews,
      smartTextState
    });
    containerProps.updateContextState({ elementPreviews });
  },

  /**
   * Copy the selected elements, update the design data and select the new elements.
   * @param {object} containerProps - Props of the calling container.
   */
  onElementsCopy(containerProps) {
    Logger.info("EditorElementOps.onElementsCopy called");
    const { designData, selectedItems } = containerProps;
    const { designData: updatedDesignData, selectedItems: updatedSelection } =
      EditorElementsOps.copyElements(selectedItems, designData);

    // Check if any of the duplicated elements is a table2 element
    const hasTable2Element = selectedItems.some(item => {
      const element = designData.getElement(item.itemId);
      return element && element.type === "table2";
    });

    const contextUpdate = hasTable2Element
      ? { context: { selectedTable2CellIds: [] } }
      : {};

    containerProps.onSave(updatedDesignData, {});
    containerProps.updateContextState({
      designData: updatedDesignData,
      selectedItems: updatedSelection,
      ...contextUpdate
    });
  },

  /**
   * Update the selected items with the provided previews.
   * @param {object} containerProps - Props of the calling container
   * @param {object} previewUpdates - An object containing all the previews to base the
   * selectedItem updates on.
   */
  onElementsPreview(previewUpdates, containerProps) {
    const { designData, selectedItems } = containerProps;
    const updatedSelectedItems =
      EditorElementsOps.updateSelectedItemsFromPreviews(
        designData,
        selectedItems,
        previewUpdates
      );

    const updatedSelection = updatedSelectedItems.filter(x => x);
    containerProps.updateContextState({ selectedItems: updatedSelection });
  },

  /**
   * Persist the updates in the element previews to the design data.
   * @param {object} containerProps - Props of the calling container
   */
  onElementPreviewPersist(containerProps) {
    const { designData, elementPreviews } = containerProps;

    if (!elementPreviews || Object.keys(elementPreviews).length === 0) {
      return;
    }

    const updatedDesignData = EditorElementsOps.updateElements(
      designData,
      elementPreviews
    );

    enqueuePreviewPropagation(
      updatedDesignData,
      elementPreviews,
      containerProps
    );

    containerProps.updateContextState({
      designData: updatedDesignData,
      elementPreviews: {}
    });
    containerProps.onSave(updatedDesignData, {});
  },

  /**
   * @desc flips an image instruction (grid cell, photo frame cell) based on axis
   * @param {object} designData - DesignData for the design containing the image instruction.
   * @param {Array<object>} selectedItems - Array of selected item objects.
   * @param {string} axis - x or y. Passed in from corresponding flipping button
   * @param {string} domId - the id representing the selected grid cell
   * ^ can also be located in context
   * @returns {object} - updated copy of the designData object
   */
  flipImageInstruction(designData, selectedItems, axis, domId) {
    // only one selected element when single cell has been selected
    const selectedElementData = cloneDeep(
      designData.getElement(selectedItems[0].itemId)
    );
    const selectedImageInstruction = selectedElementData.imageInstructions.find(
      instruction => instruction.domId === domId
    );

    if (!selectedImageInstruction) return;

    // get updated top/left values based on axis flipping
    const elementUpdates = flipSelectionOnAxis(axis, [
      selectedImageInstruction
    ]);
    // updated values returned with imageInstruction uniqueId as key
    const updatedValues = elementUpdates[selectedImageInstruction.uniqueId];

    // spread new values with existing image instruction
    const updatedImageInstruction = {
      ...selectedImageInstruction,
      ...updatedValues
    };

    // find index for original image instruction and replace with updated instruction
    const selectedImageInstructionIndex =
      selectedElementData.imageInstructions.findIndex(
        instruction => instruction.domId === domId
      );
    selectedElementData.imageInstructions.splice(
      selectedImageInstructionIndex,
      1,
      updatedImageInstruction
    );

    return designData.updateImageInstructions({
      elementId: selectedElementData.uniqueId,
      imageInstructions: selectedElementData.imageInstructions
    });
  },

  /**
   * Caret-mode kerning: nudge the kerning override for the pair to the LEFT
   * of the caret. Per-keystroke writes go to elementPreviews; the trailing
   * 500ms-debounced commit dispatches the heavy `onSelectedElementsAttributesChange`.
   */
  adjustKerningAtCaret(delta, caretIndex) {
    const target = resolveKerningTarget(this.props);
    if (!target) return;
    const idx = caretIndex - 1;
    if (idx < 0) return;
    const current = alignOverridesToLiveText(
      this,
      target,
      readActiveOverrides(this.props, target)
    );
    const next = adjustKerningOverride(current, idx, delta);
    writePreviewOverrides(this, target, next);
    dispatchKerningHud(this, (next && next[idx]) || 0);
    ensureKerningCommit(this, target)();
  },

  /**
   * Range-mode tracking: apply delta to every override key in [start, end).
   * Whole-text selection delegates to the existing letterSpacing slider path
   * so the global value updates and no per-pair overrides are written.
   * For table2Cell targets, letterSpacing lands on cells[cellId].letterSpacing.
   */
  adjustTrackingForRange(delta, rangeStart, rangeEnd) {
    const target = resolveKerningTarget(this.props);
    if (!target) return;
    if (rangeEnd <= rangeStart) return;
    const plainTextLen = resolveActiveText(this, target).length;
    if (rangeStart === 0 && rangeEnd >= plainTextLen) {
      const unit = readPersistedUnit(this.props, target);
      const nextLetterSpacing = ((unit && unit.letterSpacing) || 0) + delta;
      EditorElementsOps.onSelectedElementsAttributesChange.call(
        this,
        buildLetterSpacingAttributes(this, target, nextLetterSpacing),
        targetSelectedItem(target)
      );
      return;
    }
    const current = alignOverridesToLiveText(
      this,
      target,
      readActiveOverrides(this.props, target)
    );
    const next = adjustKerningOverrideRange(
      current,
      rangeStart,
      rangeEnd,
      delta
    );
    writePreviewOverrides(this, target, next);
    dispatchKerningHud(this, (next && next[rangeStart]) || 0);
    ensureKerningCommit(this, target)();
  },

  /**
   * Caret-mode reset: clear the override for the pair to the LEFT of the caret.
   * Range-mode reset clears every key in [start, end).
   */
  resetKerningAtCaret(caretIndex) {
    const target = resolveKerningTarget(this.props);
    if (!target) return;
    const idx = caretIndex - 1;
    if (idx < 0) return;
    const current = alignOverridesToLiveText(
      this,
      target,
      readActiveOverrides(this.props, target)
    );
    const next = mergeKerningOverride(current, idx, 0);
    writePreviewOverrides(this, target, next);
    dispatchKerningHud(this, 0);
    ensureKerningCommit(this, target)();
  },

  resetTrackingForRange(rangeStart, rangeEnd) {
    const target = resolveKerningTarget(this.props);
    if (!target) return;
    if (rangeEnd <= rangeStart) return;
    const plainTextLen = resolveActiveText(this, target).length;
    if (rangeStart === 0 && rangeEnd >= plainTextLen) {
      // Whole-text reset hard-zeros letterSpacing so the keystroke mirrors
      // adjustTrackingForRange's whole-text branch.
      EditorElementsOps.onSelectedElementsAttributesChange.call(
        this,
        buildLetterSpacingAttributes(this, target, 0),
        targetSelectedItem(target)
      );
      dispatchKerningHud(this, 0);
      return;
    }
    let next = alignOverridesToLiveText(
      this,
      target,
      readActiveOverrides(this.props, target)
    );
    for (let i = rangeStart; i < rangeEnd; i++) {
      next = mergeKerningOverride(next, i, 0);
    }
    writePreviewOverrides(this, target, next);
    dispatchKerningHud(this, 0);
    ensureKerningCommit(this, target)();
  },

  // allow updating for multiple elements multiple attributes
  onElementsAttributesChange(elementUpdates, callback) {
    Logger.info("Editor.onElementsAttributesChange called");

    const { designData } = this.props;

    this.updateStateAndSave(
      {
        designData: designData.updateElementsAttributes({
          elementUpdates
        })
      },
      callback
    );
  },

  onSelectedElementsAttributesChange(
    attributes,
    selectedItems = this.props.selectedItems,
    callback
  ) {
    Logger.info("Editor.onElementAttributeChange called");

    const { designData } = this.props;

    const selectedElementsData = selectedItems.map(({ itemId, pageId }) => {
      return {
        ...designData.elements[itemId],
        pageId
      };
    });

    // we need to ensure that preview values are only being cleared when we are applying them or intentionally clearing them
    // get the previews for the currently selected items
    const selectedItemsWithPreviews = pick(
      this.props.elementPreviews,
      selectedItems.map(item => item.itemId)
    );
    const updatedSelectedPreviews = Object.keys(
      selectedItemsWithPreviews
    ).reduce((updatedPreviews, previewKey) => {
      const currentPreview = selectedItemsWithPreviews[previewKey];
      // remove the new saving attributes from the preview so we don't get overlaps of preview data
      return {
        ...updatedPreviews,
        [previewKey]: omit(currentPreview, Object.keys(attributes))
      };
    }, {});

    const hasPermittedAttribute = Object.keys(attributes).some(key =>
      TEXTBOX_COLLECTION_PROPAGATION_ACTIONS.includes(key)
    );
    // if design is part of a collection and permitted textbox attributes were updated
    // add action to propagation queue
    if (
      this.props.collectionDesigns.length > 1 &&
      selectedElementsData.every(element => element.type === "textbox") &&
      hasPermittedAttribute
    ) {
      this.props.addActionToPropagationQueue({
        selectedElementsData,
        actionType: "textboxAttributeChange",
        attributes
      });
    }

    // Generic attribute propagation (angle, opacity, color, etc.). Canvas
    // interactions like the rotate handle reach this editor-level op rather
    // than an action bar container, so the enqueue has to happen here too.
    ActionBarOps.enqueueAttributePropagation({
      designData,
      selectedItems,
      attributes,
      collectionDesigns: this.props.collectionDesigns,
      addActionToPropagationQueue: this.props.addActionToPropagationQueue
    });

    // persist the current elementPreviews as well as updated
    const updatedPreviews = {
      ...this.state.elementPreviews,
      ...updatedSelectedPreviews
    };

    const updatedDesignData = designData.updateElementsAttribute(
      {
        elementsId: selectedItems.map(item => item.itemId),
        attributes
      },
      callback
    );

    this.updateStateAndSave({
      designData: updatedDesignData,
      lastAttributeChanges: selectedItems.map(({ itemId, pageId }) => ({
        attributes,
        uniqueId: itemId,
        pageId,
        originalElement: designData.elements[itemId]
      })),
      elementPreviews: updatedPreviews,
      selectedItems: this.props.selectedItems.map(item => ({
        ...item,
        preview: {}
      }))
    });
  },

  /**
   * Flip a selected item along the provided axis.
   * @param {string} axis - 'x' or 'y' access
   * @param {object} containerProps - Calling container props.
   */
  onFlipSelection(axis, containerProps) {
    Logger.info("EditorElementsOps.onFlipSelection called");
    const { selectedItems, designData } = containerProps;

    const selectedElements = [];
    selectedItems.forEach(selectedItem => {
      const dataElement = designData.getElement(selectedItem.itemId);
      if (dataElement.type === "group") {
        dataElement.elementsOrder.forEach(elementId => {
          selectedElements.push(designData.getElement(elementId));
        });
      } else {
        selectedElements.push(dataElement);
      }
    });

    const elementUpdates = flipSelectionOnAxis(axis, selectedElements);
    ActionBarOps.onElementsAttributesChange(elementUpdates, containerProps);
  },

  isAnySelectedElementRestrictedForPosition() {
    return this.getSelectedAsElements().some(element => !element.isResizable());
  },

  /**
   * Take the selected items and select grouped elements instead of groups where applicable
   */
  flattenSelection(selectedItems, designData) {
    const selectedElements = selectedItems.map(selectedItem =>
      designData.getElement(selectedItem.itemId)
    );

    const newSelection = [];
    selectedElements.forEach((selectedElement, elementIndex) => {
      if (selectedElement.type === "group") {
        selectedElement.elementsOrder.forEach(groupElementId => {
          const groupElement = designData.getElement(groupElementId);
          newSelection.push({
            groupId: groupElement.groupId,
            itemId: groupElement.uniqueId,
            pageId: selectedItems[elementIndex].pageId
          });
        });
      } else {
        newSelection.push(selectedItems[elementIndex]);
      }
    });
    return newSelection;
  },

  getSelectedAsElements() {
    const { selectedItems, designData } = this.props;

    return selectedItems.map(selectedItem =>
      designData.getElement(selectedItem.itemId)
    );
  },

  /**
   * Get the restrictions for each selected item and return an array of selected items with their
   * restrictions map populated.
   * @param {object} designData - The design data to retrieve the elements from.
   * @param {Array<object>} selectedItems - Array of selected items
   * @returns {Array<object>} array of selected items with
   */
  getSelectedElementsWithRestrictions(designData, selectedItems) {
    return selectedItems.map(item => {
      return designData.getElementWithRestrictions(
        Object.assign({}, item, { uniqueId: item.itemId })
      );
    });
  },

  /**
   * @desc - takes any combination of x and y axis offsets and applies
   * these offsets to the positions of all selected elements,
   * designed to only make a single call to updateStateAndSave in order
   * to avoid having too many page refreshes
   * @param {Number} offsetX - the amount to offset the x axis by
   * @param {Number} offsetY - the amount to offset the y axis by
   */
  onSelectedElementsChangePosition({ offsetX, offsetY }) {
    const { selectedItems, designData } = this.props;

    const performMovement = (item, index) => {
      const { top, left } = item;
      const attributes = {};
      if (offsetY) attributes.top = top + offsetY;
      if (offsetX) attributes.left = left + offsetX;

      /* get the updated designData object */
      const data = designData.updateElementsAttribute({
        elementsId: [item.uniqueId],
        attributes
      });

      /* if not the first element then just add the data for the element */
      if (index > 0)
        data.elements = { [item.uniqueId]: data.elements[item.uniqueId] };
      return data;
    };

    /* map through the selectedItems and apply the x or y offset */
    const updateData = selectedItems
      .map((element, index) => {
        const item = designData.getElement(element.itemId);
        if (item.type === "group") {
          /* perform the movement action on all nested elements */
          return item
            .getElements()
            .map((groupNestedElement, i) =>
              performMovement(groupNestedElement, index + i)
            );
        }
        /* if not a group, move it */
        return performMovement(item, index);
      })
      /* merge all group elements where needed */
      .reduce((p, c) => p.concat(Array.isArray(c) ? c : [c]), []);

    const update = updateData[0];
    /* join all of the updated designData objects elements together */
    update.elements = Object.assign(
      {},
      ...updateData.map(data => data.elements)
    );
    this.updateStateAndSave({
      designData: update
    });
  },

  onElementsRestrictionChange(
    { attribute, elementsId, value },
    containerProps
  ) {
    Logger.info("EditorElementsOps.onElementsRestrictionChange called");

    const { designData, selectedItems } = containerProps;

    const updatedDesignData = designData.toggleElementsRestriction({
      elementsId: elementsId || selectedItems.map(item => item.itemId),
      attribute,
      value
    });

    containerProps.updateContextState({ designData: updatedDesignData });
    containerProps.onSave(updatedDesignData, {});

    return updatedDesignData;
  },

  /**
   * Update the position of the selected item within the designData and return the updated design
   * data.
   *
   * @param {object} moveAction - action to perform on the object
   * @param {object} containerProps - Container properties for the calling container
   */
  onElementPositionChange(moveAction, containerProps) {
    Logger.info("EditorElementOps.onElementPositionChange called");

    const { designData, selectedItems } = containerProps;
    const updatedDesignData = designData.changeElementPosition({
      elementId: selectedItems[0].itemId,
      groupId: selectedItems[0].groupId,
      pageId: selectedItems[0].pageId,
      moveAction: moveAction
    });
    containerProps.updateContextState(updatedDesignData);
    containerProps.onSave(updatedDesignData, {});
  },

  deleteItem({ id, pageId, groupId }) {
    Logger.info("Editor.deleteItem called");
    const { designData } = this.props;

    // Enqueue BEFORE the mutation so matchElementInDesign's content fallback
    // can still read the element's value/src/etc. from designData.elements.
    ActionBarOps.enqueueDeleteElementPropagation({
      elements: [{ itemId: id, pageId, groupId }],
      designData,
      collectionDesigns: this.props.collectionDesigns,
      addActionToPropagationQueue: this.props.addActionToPropagationQueue
    });

    this.updateStateAndSave({
      designData: designData.deleteElement({ elementId: id, groupId, pageId }),
      selectedItems: [],
      isDragging: false
    });
  },

  /**
   * Select a single items.
   * @param {string} itemId - Item to be selected.
   * @param {string} pageId - ID of the page the selected item is on
   * @param {string} groupId - ID of the group the item belongs to.
   * @param {number} pageIndex - index of the page.
   * @param {boolean} append - whether to append the item to the selection
   * @param {TextField} textField - A TextField that should be selected.
   * @param {TextField} isQuickInput - Has been selected by the quick input bar.
   * @param {object} containerProps - Calling container properties
   */
  onSelectItem(
    {
      itemId = null,
      pageId = null,
      groupId,
      pageIndex,
      append,
      textField,
      isQuickInput = false,
      selectedCellId
    },
    containerProps
  ) {
    Logger.info("EditorElementOps.onSelectItem called");
    const { actionbar, designData, selectedItems } = containerProps;

    if (textField) {
      setTimeout(() => {
        EditorTableOps.selectTableTextField(textField);
      }, 0);
    }

    /* When a group is selected and shift click through to unselect individual elements */
    if (
      append &&
      selectedItems.length === 1 &&
      selectedItems[0].itemId === groupId
    ) {
      const groupElements = designData.elements[groupId].elementsOrder;
      const remainingGroupElements = removeItem(
        groupElements,
        groupElements.indexOf(itemId)
      );

      EditorElementsOps.onSelectItems(
        {
          itemsList: remainingGroupElements.map(id => ({
            elementdata: { uniqueId: id, groupId }
          }))
        },
        containerProps
      );
      return;
    }

    const itemIndex = selectedItems.findIndex(item => item.itemId === itemId);

    const isItemAlreadySelected = itemIndex !== -1;

    /* cancel it to avoid re-render if item is already selected */
    if (selectedItems.length === 1 && isItemAlreadySelected) {
      return;
    }

    if (isItemAlreadySelected && append) {
      const actionBarState = {
        ...actionbar,
        buttonActive: null
      };
      const baseEditorState = {
        selectedItems: removeItem(selectedItems, itemIndex)
      };
      containerProps.updateContextState({
        ...baseEditorState,
        context: {}
      });
      containerProps.updateActionBarState(actionBarState);
      return;
    }

    if (!append) {
      const _pageId =
        pageId ||
        designData.pagesOrder[pageIndex] ||
        designData.getElementPageId(itemId);

      const actionBarState = {
        ...containerProps.actionbar,
        buttonActive: null
      };

      // On mobile, clear text edit mode when selecting a new element to
      // prevent re-entering edit mode when a textbox is tapped again
      const context =
        containerProps.isMobile && containerProps.context?.isInTextEditMode
          ? { isInTextEditMode: false }
          : {};

      const baseEditorState = {
        selectedItems: [
          {
            itemId,
            groupId,
            pageId: _pageId
          }
        ],
        context
      };

      // open up QR Code popout when element is selected
      if (baseEditorState.selectedItems.length === 1) {
        const elementData = designData.getElement(itemId);

        if (elementData.type === "table2" && selectedCellId) {
          actionBarState.buttonActive = "table2Cell";
          baseEditorState.context.selectedTable2CellIds = [selectedCellId];
        } else if (
          elementData.type === "qrcode" &&
          !isQuickInput &&
          !elementData.restrictions.includes("qrvalue")
        ) {
          actionBarState.buttonActive = "qrvalue";
        }
      }

      containerProps.updateContextState(baseEditorState);
      containerProps.updateActionBarState(actionBarState);
      return;
    }

    const actionBarState = {
      ...actionbar,
      buttonActive: null
    };
    const baseEditorState = {
      selectedItems: [
        ...containerProps.selectedItems,
        {
          itemId,
          groupId,
          pageId: pageId || designData.pagesOrder[pageIndex]
        }
      ]
    };

    containerProps.updateContextState({
      ...baseEditorState,
      context: {}
    });
    containerProps.updateActionBarState(actionBarState);
  },

  /**
   * Select multiple items.
   * @param {Array<object>} itemsList - Items to be selected.
   * @param {object} containerProps - Calling container properties
   */
  onSelectItems({ itemsList }, containerProps) {
    Logger.info("EditorElementOps.onSelectItems called");
    const actionBarState = {
      ...containerProps.actionbar,
      buttonActive: null
    };
    const selectableItems = itemsList.filter(item => {
      const element = item.elementdata?.uniqueId
        ? containerProps.designData.getElement(item.elementdata.uniqueId)
        : item.elementdata;

      return !isDielineGuideElement(element);
    });

    const baseEditorState = {
      selectedItems: selectableItems.map(item => {
        return {
          itemId: item.elementdata.uniqueId,
          groupId: item.elementdata.groupId,
          pageId: containerProps.currentPageId
        };
      })
    };
    containerProps.updateContextState({
      ...baseEditorState,
      context: {}
    });
    containerProps.updateActionBarState(actionBarState);
  },

  /**
   * Toggle the visibility of the provided element.
   * @param {string} elementId - ID of the element to toggle.
   * @param {boolean} isGroupHidden - Is the containing group hidde?
   * @param {object} containerProps - Calling container props.
   */
  toggleHideElement({ elementId, isGroupHidden }, containerProps) {
    Logger.info("EditorElementsOps.toggleHideElement called");
    const { designData } = containerProps;

    const updatedDesignData = designData.toggleHideElement({
      elementId,
      isGroupHidden
    });
    containerProps.updateContextState(updatedDesignData);

    // Reset previews for selected items
    const updatedSelectedItems = containerProps.selectedItems.map(item => ({
      ...item,
      preview: {}
    }));
    const updatedContextState = {
      selectedItems: updatedSelectedItems
    };
    containerProps.onSave(updatedDesignData, updatedContextState);

    // Enqueue the visibility change on the "Update Collection" propagation
    // queue so show/hide replays across sibling designs.
    const updatedElement = updatedDesignData.getElement(elementId);
    if (
      updatedElement &&
      containerProps.addActionToPropagationQueue &&
      containerProps.collectionDesigns
    ) {
      ActionBarOps.enqueueAttributePropagation({
        designData: updatedDesignData,
        selectedItems: [
          {
            itemId: elementId,
            pageId: updatedDesignData.getElementPageId(elementId)
          }
        ],
        attributes: { isHidden: updatedElement.isHidden },
        collectionDesigns: containerProps.collectionDesigns,
        addActionToPropagationQueue: containerProps.addActionToPropagationQueue
      });
    }
  },

  /**
   * Toggle the mute state of the provided audio element.
   * @param {string} elementId - ID of the element to toggle mute.
   * @param {object} containerProps - Calling container props.
   */
  toggleMuteElement({ elementId }, containerProps) {
    Logger.info("EditorElementsOps.toggleMuteElement called");
    const { designData, currentPageId } = containerProps;

    const element = designData.getElement(elementId);
    if (!element || (element.type !== "video" && element.type !== "audio")) {
      return;
    }

    const isCurrentlyMuted = !!element.isMuted; // default false (unmuted)
    const nextIsMuted = !isCurrentlyMuted;
    const updatedDesignData = designData.updateElementsAttributes({
      pageId: currentPageId,
      elementUpdates: {
        [element.uniqueId]: {
          isMuted: nextIsMuted
        }
      }
    });

    containerProps.updateContextState({
      designData: updatedDesignData
    });

    // Reset previews for selected items
    const updatedSelectedItems = containerProps.selectedItems.map(item => ({
      ...item,
      preview: {}
    }));
    const updatedContextState = {
      selectedItems: updatedSelectedItems
    };
    containerProps.onSave(updatedDesignData, updatedContextState);

    // Enqueue the mute change on the propagation queue so it replays across
    // sibling designs. Gate on `video` locally so a future `audio` entry in
    // PROPAGATABLE_ATTRIBUTES_BY_TYPE can't accidentally enable audio-mute
    // propagation — there is no sibling-mute UX for audio.
    if (
      element.type === "video" &&
      containerProps.addActionToPropagationQueue &&
      containerProps.collectionDesigns
    ) {
      ActionBarOps.enqueueAttributePropagation({
        designData: updatedDesignData,
        selectedItems: [
          {
            itemId: elementId,
            pageId:
              currentPageId || updatedDesignData.getElementPageId(elementId)
          }
        ],
        attributes: { isMuted: nextIsMuted },
        collectionDesigns: containerProps.collectionDesigns,
        addActionToPropagationQueue: containerProps.addActionToPropagationQueue
      });
    }
  },

  /**
   * Update the design data elements based on the previews provided.
   *
   * @param {object} designData - Design data to update.
   * @param {object} elementPreviews - Element previews to base the design updates on.
   * @returns {object} updated designData.
   */
  updateElements(designData, elementPreviews) {
    if (!Object.keys(elementPreviews).length) {
      return designData;
    }

    // make a local clone to avoid mutation
    let _elementPreviews = cloneDeep(elementPreviews);

    // map through and check if any elements are pages and update them separately while removing
    // them from out update object
    Object.entries(_elementPreviews).forEach(([elementId]) => {
      if (designData.pages[elementId]) {
        designData = designData.updatePageAttribute({
          pageId: elementId,
          attributes: _elementPreviews[elementId]
        });
        delete _elementPreviews[elementId];
      }
    });

    if (Object.keys(_elementPreviews).length) {
      // now that we just have element previews we can just bulk update them
      designData = designData.updateElementsAttributes({
        elementUpdates: _elementPreviews
      });
    }

    return designData;
  },

  /**
   * Update the selectedItems with the data provided in the elementPreviews.
   *
   * @param {object} designData - DesignData to pull additional element information from.
   * @param {Array} selectedItems - Array of selectedItem objects to update.
   * @param {object} elementPreviews - An object containing all the previews to base the
   * selectedItem updates on.
   * @return {Array} of updated selectedItem objects.
   */
  updateSelectedItemsFromPreviews(designData, selectedItems, elementPreviews) {
    return Object.keys(elementPreviews).map(elementId => {
      let selectedItem = selectedItems.find(item => item.itemId === elementId);
      if (!selectedItem) {
        // attempt to get the item from designData
        const dataElement = designData.getElement(elementId);
        // when still empty just return undefined
        if (!dataElement) return undefined;

        const dataElementPageId = designData.getElementPageId(elementId);
        selectedItem = {
          groupId: dataElement.groupId,
          itemId: dataElement.uniqueId,
          pageId: dataElementPageId
        };
      }

      const attributes = elementPreviews[elementId];
      selectedItem = {
        ...selectedItem,
        preview: {
          ...attributes
        }
      };

      const element = designData.getElement(elementId);

      // when the preview is image instructions we want to replace without removing unchanged ones
      if (
        Object.keys(attributes).includes("imageInstructions") &&
        attributes.imageInstructions.length
      ) {
        const previewInstructions = attributes.imageInstructions;
        const elementInstructions = element.imageInstructions;

        const originalInstructionsByDomId = elementInstructions.reduce(
          (combinedInstructions, currentInstruction) => ({
            ...combinedInstructions,
            [currentInstruction.domId]: currentInstruction
          }),
          {}
        );

        previewInstructions.forEach(previewInstruction => {
          originalInstructionsByDomId[previewInstruction.domId] =
            previewInstruction;
        });

        selectedItem.preview.imageInstructions = Object.values(
          originalInstructionsByDomId
        );
      }

      return selectedItem;
    });
  },

  /**
   * Update the previews of the selectedItems
   * @param {Array} selectedItems - Selected items to update the previews in.
   * @param {object} elementPreviews - containing the previews to update the selected items
   * @param {object} attributes - The element attributes to be updated
   * @return {{}}
   */
  updatePreviews(selectedItems, elementPreviews, attributes) {
    // we need to ensure that preview values are only being cleared when we are applying them or
    // intentionally clearing them get the previews for the currently selected items
    const selectedItemsWithPreviews = pick(
      elementPreviews,
      selectedItems.map(item => item.itemId)
    );
    return Object.keys(selectedItemsWithPreviews).reduce(
      (updatedPreviews, previewKey) => {
        const currentPreview = selectedItemsWithPreviews[previewKey];
        // remove the new saving attributes from the preview, so we don't get overlaps of preview data
        return {
          ...updatedPreviews,
          [previewKey]: omit(currentPreview, Object.keys(attributes))
        };
      },
      {}
    );
  },

  /**
   * Update the attributes on the selected items and return the updated items.
   *
   * @param {object} designData - Design data to pull the elements from.
   * @param {Array} selectedItems - Selected items.
   * @param {object} context - the editors current context.
   * @param {object} attributes - The attributes and the new values they should be set to.
   * @return {Array} of updated selection items.
   */
  updateSelectedItemAttributes(designData, selectedItems, context, attributes) {
    return selectedItems
      .map(selectedItem => ({
        ...selectedItem,
        preview: {
          ...attributes
        }
      }))
      .map(selectedItem => {
        const element = designData.getElement(selectedItem.itemId);
        // when the preview is image instructions we want to replace without removing unchanged ones
        if (
          Object.keys(attributes).includes("imageInstructions") &&
          attributes.imageInstructions.length
        ) {
          const previewInstructions = attributes.imageInstructions;
          const elementInstructions = element.imageInstructions;

          const originalInstructionsByDomId = elementInstructions.reduce(
            (combinedInstructions, currentInstruction) => ({
              ...combinedInstructions,
              [currentInstruction.domId]: currentInstruction
            }),
            {}
          );

          previewInstructions.forEach(previewInstruction => {
            originalInstructionsByDomId[previewInstruction.domId] =
              previewInstruction;
          });

          selectedItem.preview.imageInstructions = Object.values(
            originalInstructionsByDomId
          );
        }

        // handle updating height when preview changes for a textbox
        if (
          element.type === "textbox" &&
          !element.restrictions.includes("textBoundary") &&
          !context.isCroppingTextMaskImage &&
          !selectedItem.preview.height
        ) {
          selectedItem.preview.height = element.getHeight({
            ...selectedItem.preview
          });
        }

        return selectedItem;
      });
  }
};

/**
 * Build a flattened itemsList for selection, expanding groups into their
 * individual child elements with the correct groupId.
 *
 * @param {string[]} elementIds - Top-level element IDs (e.g. page.elementsOrder)
 * @param {object} designData - Design data object with getElement method
 * @returns {Array<{elementdata: object}>} Flattened itemsList for onSelectItems
 */
export const buildFlattenedItemsList = (elementIds, designData) => {
  const itemsList = [];
  elementIds.forEach(elementId => {
    const elementdata = designData.getElement(elementId);

    if (elementdata.type === "group") {
      elementdata.elementsOrder.forEach(groupElementId => {
        const groupElement = designData.getElement(groupElementId);
        if (isDielineGuideElement(groupElement)) return;

        itemsList.push({
          elementdata: {
            uniqueId: groupElement.uniqueId,
            groupId: groupElement.groupId
          }
        });
      });
    } else {
      if (isDielineGuideElement(elementdata)) return;

      itemsList.push({ elementdata });
    }
  });
  return itemsList;
};

export default EditorElementsOps;
